import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import RolesHiringStrip from "@/components/RolesHiringStrip";
import {
    ChalkboardTeacher, ArrowRight, MagnifyingGlass, Sparkle,
    Star, Lightning, LockKey, CheckCircle, Timer,
} from "@phosphor-icons/react";

const POPULAR = [
    { role: "Software Engineer",   yrs: 3, focus: "technical" },
    { role: "Product Manager",     yrs: 4, focus: "behavioral" },
    { role: "Data Scientist",      yrs: 3, focus: "technical" },
    { role: "Engineering Manager", yrs: 7, focus: "mixed" },
    { role: "Solutions Architect", yrs: 8, focus: "system-design" },
];

const CATEGORY_STYLE = {
    behavioral:      { pill: "bg-cobalt text-white",    label: "Behavioral" },
    technical:       { pill: "bg-splash text-ink",      label: "Technical" },
    situational:     { pill: "bg-emerald-500 text-white", label: "Situational" },
    "system-design": { pill: "bg-amber-500 text-white", label: "System Design" },
};

export default function InterviewPrepPublic() {
    useSeo({
        title: "Free Interview Prep · AI-Generated Questions for Indian Job Roles | NaukriDaddy",
        description: "5 free AI-generated interview questions for any role — behavioral, technical, or system design. Upgrade to a ₹199 personalised session that reads your resume.",
        canonical: "https://naukridaddy.com/interview-prep",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "AI Interview Prep · NaukriDaddy",
            "description": "AI-generated interview questions personalised to your role, years of experience, and desired focus (behavioral, technical, or system design).",
            "provider": {
                "@type": "Organization",
                "name": "Naukridaddy HR Solutions Private Limited",
                "url": "https://naukridaddy.com",
            },
            "offers": [
                { "@type": "Offer", "name": "5-question preview", "price": "0", "priceCurrency": "INR" },
                { "@type": "Offer", "name": "25-question personalised session", "price": "199", "priceCurrency": "INR" },
            ],
        },
    });
    const [form, setForm] = useState({ role: "", years_experience: 3, focus: "mixed" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const run = async (payload) => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/public/interview-preview", {
                role: payload.role,
                years_experience: Number(payload.years_experience) || 3,
                focus: payload.focus || "mixed",
            });
            setResult(data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Couldn't fetch preview — try again.");
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="interview-prep-public-page">
            {/* Header */}
            <header className="border-b border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="font-heading text-2xl font-black tracking-tighter text-ink">NaukriDaddy</Link>
                    <Link to="/candidate/signup" data-testid="prep-header-cta"
                          className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-ink">
                        Get full session · ₹199 →
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-6">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">
                    <Sparkle size={11} weight="fill" className="inline mb-0.5 mr-1" />
                    AI-powered · 5 free preview questions
                </p>
                <h1 className="font-heading text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] mb-4">
                    Nail your next <span className="text-cobalt">interview.</span>
                </h1>
                <p className="text-base sm:text-lg text-[#404040] max-w-2xl leading-relaxed">
                    Try 5 real interview questions tailored to your role — with AI-generated
                    answer hints. Loved it? Unlock 25 personalised questions in a paid
                    session (₹199) that also references your resume.
                </p>
            </section>

            {/* Form */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-6">
                <form onSubmit={(e) => { e.preventDefault(); run(form); }}
                      data-testid="prep-form"
                      className="bg-white border-2 border-ink p-4 sm:p-6">
                    <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Role you&apos;re interviewing for</label>
                            <input required minLength={2} maxLength={80}
                                   data-testid="prep-role-input"
                                   value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                                   placeholder="e.g. Senior Software Engineer"
                                   className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Years</label>
                            <input type="number" min={0} max={40} step={0.5}
                                   data-testid="prep-years-input"
                                   value={form.years_experience} onChange={(e) => setForm(f => ({ ...f, years_experience: e.target.value }))}
                                   className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Focus</label>
                            <select data-testid="prep-focus-select"
                                    value={form.focus} onChange={(e) => setForm(f => ({ ...f, focus: e.target.value }))}
                                    className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink bg-white">
                                <option value="mixed">Mixed</option>
                                <option value="behavioral">Behavioral</option>
                                <option value="technical">Technical</option>
                                <option value="system-design">System design</option>
                            </select>
                        </div>
                        <button type="submit" disabled={loading || form.role.trim().length < 2}
                                data-testid="prep-run-btn"
                                className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50">
                            <MagnifyingGlass size={13} weight="bold" />
                            {loading ? "Generating…" : "Get 5 free"}
                        </button>
                    </div>
                </form>

                {/* Popular */}
                <div className="flex flex-wrap gap-2 mt-4" data-testid="prep-popular">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] w-full mb-1">Popular</p>
                    {POPULAR.map((p, i) => (
                        <button key={i} data-testid={`prep-popular-${i}`}
                                onClick={() => { setForm({ role: p.role, years_experience: p.yrs, focus: p.focus }); run(p); }}
                                className="text-[11px] px-3 py-1.5 bg-[#FAFAFA] hover:bg-ink hover:text-white border border-[#E5E5E5] transition-colors">
                            {p.role} · {p.yrs}y · {p.focus}
                        </button>
                    ))}
                </div>
            </section>

            {/* Loading */}
            {loading && (
                <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-8 text-center text-sm text-[#525252]" data-testid="prep-loading">
                        <Sparkle size={20} weight="fill" className="mx-auto mb-2 text-splash animate-pulse" />
                        Preparing questions tailored to your role…
                    </div>
                </section>
            )}

            {/* Results */}
            {result && (
                <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="prep-result">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        {result.questions.length} preview questions for {result.role}
                    </p>
                    <div className="space-y-3">
                        {result.questions.map((q, i) => {
                            const style = CATEGORY_STYLE[q.category] || CATEGORY_STYLE["behavioral"];
                            return (
                                <div key={i} data-testid={`prep-question-${i}`}
                                     className="bg-white border border-[#E5E5E5] p-5 sm:p-6">
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Q{i + 1}</p>
                                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 ${style.pill}`}>
                                            {style.label}
                                        </span>
                                    </div>
                                    <h3 className="font-heading text-lg sm:text-xl font-black text-ink leading-tight mb-3">
                                        {q.q}
                                    </h3>
                                    {q.why_asked && (
                                        <p className="text-xs text-[#525252] mb-3 italic border-l-2 border-splash pl-3">
                                            <b>Why asked · </b>{q.why_asked}
                                        </p>
                                    )}
                                    {q.answer_hint && (
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1">Hint</p>
                                            <p className="text-sm text-[#404040] leading-relaxed">{q.answer_hint}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Paywall teaser */}
                    <div className="bg-ink text-white p-6 sm:p-8 mt-6" data-testid="prep-paywall-cta">
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2">
                                    <LockKey size={11} weight="fill" className="inline mb-0.5 mr-1" /> Want 25 personalised questions?
                                </p>
                                <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-3">
                                    Full sessions reference <span className="text-splash">your resume</span>.
                                </h3>
                                <ul className="space-y-1.5 text-sm text-white/80 mb-4">
                                    <li className="flex items-start gap-2"><CheckCircle size={13} weight="fill" className="text-splash mt-1 shrink-0" /> 25 questions per session (5× more depth)</li>
                                    <li className="flex items-start gap-2"><CheckCircle size={13} weight="fill" className="text-splash mt-1 shrink-0" /> STAR-format answer templates from your actual experience</li>
                                    <li className="flex items-start gap-2"><CheckCircle size={13} weight="fill" className="text-splash mt-1 shrink-0" /> Paste a JD → questions tuned to that exact role</li>
                                    <li className="flex items-start gap-2"><CheckCircle size={13} weight="fill" className="text-splash mt-1 shrink-0" /> &ldquo;Interview Ready&rdquo; badge for recruiters to see</li>
                                </ul>
                            </div>
                            <div className="text-right">
                                <p className="font-heading text-5xl font-black tracking-tighter text-splash">₹199</p>
                                <p className="text-[10px] text-white/50 mb-3">per session</p>
                                <Link to="/candidate/signup" data-testid="prep-cta-signup"
                                      className="inline-flex items-center gap-2 bg-splash text-ink px-5 py-2.5 text-xs font-black uppercase tracking-wider hover:bg-white">
                                    Start free profile <ArrowRight size={13} weight="bold" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Trust strip */}
            <RolesHiringStrip
                eyebrow="LIVE ROLES · UPDATED DAILY"
                headline="These teams are hiring right now."
                subhead="Practise for the interview → apply the same day. Our AI matches your resume to any of these in seconds."
            />
            <section className="border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink mb-4">
                        Why NaukriDaddy Interview Prep
                    </h2>
                    <div className="grid gap-4 md:grid-cols-3 text-sm text-[#404040]">
                        <div>
                            <ChalkboardTeacher size={20} weight="fill" className="text-cobalt mb-2" />
                            <p className="font-black text-ink mb-1">Coach-quality questions</p>
                            <p>Modelled on senior interviewers at TCS, Infosys, Flipkart, Freshworks, Razorpay, and the top 50 Indian agencies.</p>
                        </div>
                        <div>
                            <Star size={20} weight="fill" className="text-splash mb-2" />
                            <p className="font-black text-ink mb-1">Personalised to your resume</p>
                            <p>Paid sessions read your work history and ask questions probing exactly what you built — not generic Google snippets.</p>
                        </div>
                        <div>
                            <Timer size={20} weight="fill" className="text-amber-500 mb-2" />
                            <p className="font-black text-ink mb-1">30 minutes, not 30 hours</p>
                            <p>One focused prep session covers a full interview loop. Convert your commute into interview-ready confidence.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
